import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  Download,
  FileSignature,
  RefreshCcw,
  Eye,
  Copy,
  Fingerprint,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PER_PAGE = 25;

function formatearFecha(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function RoleBadge({ role }) {
  const label =
    role === "paciente"
      ? "persona usuaria"
      : role === "medico"
      ? "especialista"
      : role || "—";
  const cls =
    role === "medico"
      ? "bg-[#0F2942] text-white"
      : role === "admin"
      ? "bg-[#2563EB] text-white"
      : "bg-muted text-foreground";
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${cls}`}
      data-testid={`firma-role-${role || "vacio"}`}
    >
      {label}
    </span>
  );
}

export default function AdminFirmasAcuerdo() {
  const { user } = useAuth();
  const isSuperadmin = Boolean(user?.is_superadmin);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [inputQ, setInputQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [descargando, setDescargando] = useState(false);
  const [detalleAbierto, setDetalleAbierto] = useState(null); // firma_id | null
  const [detalleData, setDetalleData] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      params.set("page", String(page));
      params.set("per_page", String(PER_PAGE));
      const { data } = await api.get(`/admin/acuerdo/firmas?${params}`);
      setRows(data.items);
      setTotal(data.meta.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => {
    load();
  }, [load]);

  const descargarCsv = async () => {
    setDescargando(true);
    try {
      const token =
        typeof window !== "undefined"
          ? window.localStorage.getItem("saludia_token")
          : null;
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      params.set("format", "csv");
      const resp = await fetch(`${API_BASE}/admin/acuerdo/firmas?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const disp = resp.headers.get("Content-Disposition") || "";
      const match = /filename="?([^";]+)"?/i.exec(disp);
      const filename = match ? match[1] : "firmas-acuerdo.csv";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) {
      toast.error(`No se pudo descargar el CSV: ${e.message || "error"}`);
    } finally {
      setDescargando(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const abrirDetalle = async (firmaId) => {
    setDetalleAbierto(firmaId);
    setDetalleData(null);
    setDetalleLoading(true);
    try {
      const { data } = await api.get(`/admin/acuerdo/firmas/${firmaId}`);
      setDetalleData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDetalleAbierto(null);
    } finally {
      setDetalleLoading(false);
    }
  };

  const cerrarDetalle = () => {
    setDetalleAbierto(null);
    setDetalleData(null);
  };

  const copiar = async (texto, etiqueta) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(`${etiqueta} copiado al portapapeles`);
    } catch {
      toast.error("No se pudo copiar. Selecciónalo y usa Cmd/Ctrl+C.");
    }
  };

  return (
    <div data-testid="admin-firmas-acuerdo">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-[#0F2942] flex items-center gap-2">
            <FileSignature size={18} className="text-[#2563EB]" />
            Firmas del Acuerdo
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {total} aceptación{total === 1 ? "" : "es"} del Acuerdo de Uso y Consentimiento Informado.
            Trazabilidad regulatoria (no dato clínico). IP truncada al último octeto por privacidad.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="firmas-refrescar-btn"
            className="gap-1.5"
          >
            <RefreshCcw size={14} /> Refrescar
          </Button>
          <Button
            size="sm"
            onClick={descargarCsv}
            disabled={descargando || total === 0}
            data-testid="firmas-descargar-csv-btn"
            className="bg-[#0F2942] hover:bg-[#0F2942]/90 text-white gap-1.5"
          >
            {descargando ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Descargar CSV
          </Button>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setQ(inputQ.trim());
        }}
        className="mt-4 flex items-center gap-2"
      >
        <div className="relative flex-1 max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <Input
            value={inputQ}
            onChange={(e) => setInputQ(e.target.value)}
            placeholder="Buscar por nombre firmado o email"
            className="pl-9 h-9"
            data-testid="firmas-buscar-input"
          />
        </div>
        <Button
          type="submit"
          size="sm"
          variant="outline"
          data-testid="firmas-buscar-btn"
        >
          Buscar
        </Button>
        {q && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setInputQ("");
              setPage(1);
              setQ("");
            }}
            data-testid="firmas-limpiar-btn"
          >
            Limpiar
          </Button>
        )}
      </form>

      <div className="mt-4 border border-border rounded-lg bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="firmas-tabla">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Fecha</th>
                <th className="text-left px-4 py-2 font-semibold">Nombre firmado</th>
                <th className="text-left px-4 py-2 font-semibold">Email</th>
                <th className="text-left px-4 py-2 font-semibold">Rol</th>
                <th className="text-left px-4 py-2 font-semibold">Versión</th>
                <th className="text-left px-4 py-2 font-semibold">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    <Loader2 size={14} className="inline-block animate-spin mr-2" /> Cargando firmas…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted-foreground"
                    data-testid="firmas-vacio"
                  >
                    Sin firmas para los criterios de búsqueda.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-border hover:bg-muted/40 cursor-pointer"
                    data-testid={`firma-row-${r.id}`}
                    onClick={() => abrirDetalle(r.id)}
                    title="Ver detalle de la firma"
                  >
                    <td className="px-4 py-2 text-foreground whitespace-nowrap">
                      {formatearFecha(r.fecha)}
                    </td>
                    <td className="px-4 py-2 font-medium text-[#0F2942]">
                      {r.nombre_firmado || "—"}
                      {r.nombre_cuenta && r.nombre_cuenta !== r.nombre_firmado && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          Cuenta: {r.nombre_cuenta}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground break-all">
                      {r.email || "—"}
                    </td>
                    <td className="px-4 py-2">
                      <RoleBadge role={r.role} />
                    </td>
                    <td className="px-4 py-2 text-muted-foreground font-mono text-xs">
                      {r.version_documento || "—"}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <span>{r.ip_truncada || "—"}</span>
                        <Eye
                          size={12}
                          className="text-[#2563EB] shrink-0"
                          aria-label="Ver detalle"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div
          className="mt-4 flex items-center justify-between text-xs text-muted-foreground"
          data-testid="firmas-paginacion"
        >
          <div>
            Página {page} de {totalPages} · {total} firma{total === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1 || loading}
              onClick={() => setPage(page - 1)}
              data-testid="firmas-prev-btn"
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(page + 1)}
              data-testid="firmas-next-btn"
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={detalleAbierto !== null}
        onOpenChange={(open) => (open ? null : cerrarDetalle())}
      >
        <DialogContent
          className="max-w-2xl"
          data-testid="firma-detalle-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F2942]">
              <FileSignature size={16} className="text-[#2563EB]" /> Detalle de firma
            </DialogTitle>
            <DialogDescription>
              Evento completo de aceptación electrónica del Acuerdo de Uso y Consentimiento Informado.
              {isSuperadmin && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#2563EB]/10 text-[#2563EB] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest">
                  <ShieldCheck size={10} /> Vista superadmin — IP completa visible
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {detalleLoading && (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="firma-detalle-loading">
              <Loader2 size={16} className="inline-block animate-spin mr-2" /> Cargando detalle…
            </div>
          )}

          {!detalleLoading && detalleData && (
            <div className="space-y-3 text-sm" data-testid="firma-detalle-content">
              <DetalleField label="Fecha (UTC)" value={detalleData.fecha || "—"} mono />
              <DetalleField label="Nombre firmado" value={detalleData.nombre_firmado || "—"} strong />
              {detalleData.nombre_cuenta &&
                detalleData.nombre_cuenta !== detalleData.nombre_firmado && (
                  <DetalleField label="Nombre en la cuenta" value={detalleData.nombre_cuenta} />
                )}
              <DetalleField label="Email" value={detalleData.email || "—"} mono />
              <DetalleField
                label="Rol"
                node={<RoleBadge role={detalleData.role} />}
              />
              <DetalleField label="Versión del documento" value={detalleData.version_documento || "—"} mono />
              <DetalleField
                label="IP truncada (admin)"
                value={detalleData.ip_truncada || "—"}
                mono
                testid="firma-detalle-ip-truncada"
              />
              {isSuperadmin && "ip_completa" in detalleData && (
                <DetalleField
                  label="IP completa (superadmin)"
                  value={detalleData.ip_completa || "—"}
                  mono
                  testid="firma-detalle-ip-completa"
                  onCopy={() =>
                    detalleData.ip_completa && copiar(detalleData.ip_completa, "IP")
                  }
                />
              )}
              <DetalleField
                label="User agent"
                value={detalleData.user_agent || "—"}
                mono
                small
                testid="firma-detalle-user-agent"
              />
              <div
                className="rounded-md border border-border bg-muted/30 p-3"
                data-testid="firma-detalle-hash"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Fingerprint size={12} className="text-[#2563EB]" /> Hash SHA-256 del documento firmado
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={() =>
                      detalleData.documento_hash &&
                      copiar(detalleData.documento_hash, "Hash")
                    }
                    disabled={!detalleData.documento_hash}
                    data-testid="firma-detalle-copy-hash-btn"
                  >
                    <Copy size={12} /> Copiar
                  </Button>
                </div>
                <div className="font-mono text-[11px] break-all text-foreground">
                  {detalleData.documento_hash || "(sin hash)"}
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground">
                  Fuente:{" "}
                  {detalleData.documento_hash_fuente === "persistido"
                    ? "persistido al firmar"
                    : "recalculado al vuelo (firma legacy previa a v6.10)"}
                </div>
              </div>
              <div className="text-[10px] text-muted-foreground pt-2 border-t border-border">
                ID interno:{" "}
                <span className="font-mono">{detalleData.id}</span>
                {" · "}user_id:{" "}
                <span className="font-mono">{detalleData.user_id}</span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={cerrarDetalle}
              data-testid="firma-detalle-cerrar-btn"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetalleField({ label, value, node, mono, strong, small, testid, onCopy }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-3 items-start">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        data-testid={testid}
        className={[
          "text-foreground break-all",
          mono ? "font-mono" : "",
          strong ? "font-semibold text-[#0F2942]" : "",
          small ? "text-[11px]" : "text-sm",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">{node || value}</div>
          {onCopy && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 shrink-0"
              onClick={onCopy}
              title="Copiar"
            >
              <Copy size={12} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
